import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AppShell } from '../components/layout/AppShell';
import { LoginPage } from '../features/auth/LoginPage';
import { RegisterPage } from '../features/auth/RegisterPage';
import { ForgotPasswordPage } from '../features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '../features/auth/ResetPasswordPage';
import { RequireAuth } from '../features/auth/RequireAuth';
import { MarketingLanding } from '../features/marketing/MarketingLanding';
import { PrivacyPage, DeleteAccountInfoPage } from '../features/marketing/LegalPages';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { ProtocolPage } from '../features/protocol/ProtocolPage';
import { ProtocolDiloPage } from '../features/protocol/ProtocolDiloPage';
import { ProtocolDiloVelkyPage } from '../features/protocol/ProtocolDiloVelkyPage';
import { InvoicePage } from '../features/invoice/InvoicePage';
import { DiaryPage } from '../features/diary/DiaryPage';
import { StavbyPage } from '../features/stavby/StavbyPage';
import { UkolyPage } from '../features/ukoly/UkolyPage';
import { SubdodavatelePage } from '../features/subdodavatele/SubdodavatelePage';
import { ArchivPage } from '../features/archiv/ArchivPage';
import { VadyPage } from '../features/vady/VadyPage';
import { RfiPage } from '../features/rfi/RfiPage';
import { KontrolniDnyPage } from '../features/kontrolni-dny/KontrolniDnyPage';
import { KontrolniDenEditorPage } from '../features/kontrolni-dny/KontrolniDenEditor';
import { NasdileneKdPage } from '../features/kontrolni-dny/NasdileneKdPage';
import { SdilenyKdView } from '../features/kontrolni-dny/SdilenyKdView';
import { ZmenyPage } from '../features/zmeny/ZmenyPage';
import { SoupisPage } from '../features/documents/SoupisPage';
import { ZjistovaciPage } from '../features/documents/ZjistovaciPage';
import { NabidkaPage } from '../features/documents/NabidkaPage';
import { ObjednavkaPage } from '../features/documents/ObjednavkaPage';
import { KosilkaPage } from '../features/documents/KosilkaPage';
import { ReklamacePage } from '../features/documents/ReklamacePage';
import { TelefonyPage } from '../features/bozp/TelefonyPage';
import { TraumaPage } from '../features/bozp/TraumaPage';
import { SeznameniPage } from '../features/bozp/SeznameniPage';
import { UrazyPage } from '../features/bozp/UrazyPage';
import { RizikaPage } from '../features/bozp/RizikaPage';
import { PozarniDohledPage } from '../features/bozp/PozarniDohledPage';
import { KartyZamestnancuPage } from '../features/zamestnanci/KartyZamestnancuPage';
import { FotodokumentacePage } from '../features/fotodokumentace/FotodokumentacePage';
import { HarmonogramPage } from '../features/harmonogram/HarmonogramPage';
import { ProjectDocsPage } from '../features/project-docs/ProjectDocsPage';
import { NasdileneDokumentyPage } from '../features/project-docs/NasdileneDokumentyPage';
import { VyuctovaniPage } from '../features/vyuctovani/VyuctovaniPage';
import { KnihaJizdPage } from '../features/kniha-jizd/KnihaJizdPage';
import { NotFound } from './NotFound';
import { FreshDocRoute } from '../components/document';

// Podepisování PDF táhne pdf-lib (~500 kB) — načte se až při otevření stránky.
const PodepsatPage = lazy(() =>
  import('../features/podepsat/PodepsatPage').then((m) => ({ default: m.PodepsatPage })),
);

/**
 * Routing mapa:
 *  - /          → MarketingLanding (veřejná)
 *  - /login, /register, /forgot-password, /reset-password → auth (veřejné)
 *  - /app/*     → autentizované obrazovky uvnitř AppShell
 *
 * Všech 28 obrazovek má React implementaci; data jsou zatím mock,
 * napojení na FastAPI backend (/api/*) přijde postupně.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<MarketingLanding />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/ochrana-osobnich-udaju" element={<PrivacyPage />} />
      <Route path="/smazani-uctu" element={<DeleteAccountInfoPage />} />

      <Route
        path="/app"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {/* Fáze 1 — navržené obrazovky */}
        <Route index element={<DashboardPage />} />
        <Route path="predavaci" element={<FreshDocRoute><ProtocolPage /></FreshDocRoute>} />
        <Route path="predavaci-dilo" element={<FreshDocRoute><ProtocolDiloPage /></FreshDocRoute>} />
        <Route path="predavaci-dilo-velky" element={<FreshDocRoute><ProtocolDiloVelkyPage /></FreshDocRoute>} />
        <Route path="faktura" element={<FreshDocRoute><InvoicePage /></FreshDocRoute>} />
        <Route path="denik" element={<DiaryPage />} />

        {/* Přehled */}
        <Route path="stavby" element={<StavbyPage />} />
        <Route path="ukoly" element={<UkolyPage />} />

        {/* Dokumenty */}
        <Route path="soupis" element={<FreshDocRoute><SoupisPage /></FreshDocRoute>} />
        <Route path="zjistovaci" element={<FreshDocRoute><ZjistovaciPage /></FreshDocRoute>} />
        <Route path="nabidka" element={<FreshDocRoute><NabidkaPage /></FreshDocRoute>} />
        <Route path="objednavka" element={<FreshDocRoute><ObjednavkaPage /></FreshDocRoute>} />
        <Route path="reklamace" element={<FreshDocRoute><ReklamacePage /></FreshDocRoute>} />
        <Route
          path="podepsat"
          element={
            <Suspense fallback={<div style={{ padding: 24 }}>Načítám…</div>}>
              <PodepsatPage />
            </Suspense>
          }
        />

        {/* Realizace */}
        <Route path="kontrolni-dny" element={<KontrolniDnyPage />} />
        <Route path="kontrolni-dny/nasdilene" element={<NasdileneKdPage />} />
        <Route path="kontrolni-dny/nasdilene/:stavbaId/:kdId" element={<SdilenyKdView />} />
        <Route path="kontrolni-dny/:kdId" element={<KontrolniDenEditorPage />} />
        <Route path="fotodokumentace" element={<FotodokumentacePage />} />
        <Route path="vady" element={<VadyPage />} />
        <Route path="rfi" element={<RfiPage />} />
        <Route path="harmonogram" element={<HarmonogramPage />} />
        <Route path="zmeny" element={<ZmenyPage />} />

        {/* BOZP */}
        <Route path="bozp">
          <Route path="telefony" element={<TelefonyPage />} />
          <Route path="trauma" element={<FreshDocRoute><TraumaPage /></FreshDocRoute>} />
          <Route path="seznameni" element={<SeznameniPage />} />
          <Route path="urazy" element={<UrazyPage />} />
          <Route path="rizika" element={<FreshDocRoute><RizikaPage /></FreshDocRoute>} />
          <Route path="pozarni-dohled" element={<FreshDocRoute><PozarniDohledPage /></FreshDocRoute>} />
          <Route path="karty-zamestnancu" element={<KartyZamestnancuPage />} />
        </Route>

        {/* Obchod */}
        <Route path="subdodavatele" element={<SubdodavatelePage />} />
        <Route path="kosilka" element={<FreshDocRoute><KosilkaPage /></FreshDocRoute>} />
        <Route path="vyuctovani" element={<VyuctovaniPage />} />
        <Route path="kniha-jizd" element={<KnihaJizdPage />} />
        <Route path="dokumenty-projektu" element={<ProjectDocsPage />} />
        <Route path="dokumenty-projektu/nasdilene" element={<NasdileneDokumentyPage />} />
        <Route path="archiv" element={<ArchivPage />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
